/**
 * Reporting module contracts (RBAC area: reports).
 *
 * A report definition is a saved, whitelisted query over one PSA entity: a chosen set
 * of columns plus a small set of typed filters, optionally scheduled for recurring
 * delivery by email. The backend NEVER builds SQL from these values — column and filter
 * keys are validated against the catalogs below and mapped to typed Prisma queries, so a
 * report can only ever read fields the catalog exposes.
 *
 * Keep this file free of runtime framework dependencies (shared by backend, frontend,
 * and jobs).
 */
/** Entities a report can be built from. */
export const REPORT_ENTITIES = [
    'time_entries',
    'invoices',
    'projects',
    'agreement_periods',
    'tickets',
];
export const REPORT_ENTITY_LABELS = {
    time_entries: 'Time Entries',
    invoices: 'Invoices',
    projects: 'Projects',
    agreement_periods: 'Agreement Periods',
    tickets: 'Tickets',
};
export const REPORT_FREQUENCIES = ['manual', 'interval', 'daily', 'weekly', 'monthly'];
export const REPORT_FREQUENCY_LABELS = {
    manual: 'Manual (no schedule)',
    interval: 'Multiple times a day',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
};
/** Allowed interval (minutes) range for `interval` frequency. Min matches the run-due timer. */
export const REPORT_MIN_INTERVAL_MINUTES = 15;
export const REPORT_MAX_INTERVAL_MINUTES = 1440;
export const REPORT_DEFAULT_INTERVAL_MINUTES = 240;
/**
 * "Multiple times a day" runs at a configured set of clock times (`runTimes`) in the
 * billing timezone, e.g. ['08:00','12:00','17:00']. Each time is `HH:MM` 24-hour.
 */
export const REPORT_RUN_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const REPORT_MAX_RUN_TIMES = 24;
export const REPORT_DEFAULT_RUN_TIMES = ['08:00'];
/** Validates + normalizes a list of `HH:MM` run-times: drops blanks/dupes, sorts ascending. */
export function normalizeRunTimes(times) {
    const seen = new Set();
    for (const t of times) {
        const trimmed = t?.trim();
        if (trimmed && REPORT_RUN_TIME_RE.test(trimmed))
            seen.add(trimmed);
    }
    return [...seen].sort();
}
export const REPORT_RUN_STATUSES = ['ok', 'failed', 'skipped'];
/**
 * How a report sources its rows:
 * - `builder`: the whitelisted column/filter query builder (default, safe for any user).
 * - `sql`: an admin-authored read-only SELECT executed with strong guardrails (see
 *   the backend SQL engine). Power-user escape hatch for arbitrary queries.
 */
export const REPORT_MODES = ['builder', 'sql'];
export const REPORT_MODE_LABELS = {
    builder: 'Query builder',
    sql: 'Advanced (SQL)',
};
/** Maximum length (characters) of an advanced SQL report query. */
export const REPORT_SQL_MAX_LENGTH = 8000;
// ---------------------------------------------------------------------------
// Column catalog — the complete set of columns each entity may expose. Any column
// key not present here is rejected by the backend.
// ---------------------------------------------------------------------------
export const REPORT_COLUMNS = {
    time_entries: [
        { key: 'workDate', label: 'Work Date', type: 'date' },
        { key: 'member', label: 'Member', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'project', label: 'Project', type: 'string' },
        { key: 'ticket', label: 'Ticket', type: 'string' },
        { key: 'serviceCategory', label: 'Service Category', type: 'string' },
        { key: 'billingClass', label: 'Billing Class', type: 'string' },
        { key: 'hours', label: 'Hours', type: 'number' },
        { key: 'billable', label: 'Billable', type: 'boolean' },
        { key: 'resolvedRate', label: 'Rate', type: 'money' },
        { key: 'amount', label: 'Amount', type: 'money' },
        { key: 'notes', label: 'Notes', type: 'string' },
    ],
    invoices: [
        { key: 'number', label: 'Invoice #', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'source', label: 'Source', type: 'string' },
        { key: 'issueDate', label: 'Issue Date', type: 'date' },
        { key: 'dueDate', label: 'Due Date', type: 'date' },
        { key: 'periodStart', label: 'Period Start', type: 'date' },
        { key: 'periodEnd', label: 'Period End', type: 'date' },
        { key: 'currency', label: 'Currency', type: 'string' },
        { key: 'subtotal', label: 'Subtotal', type: 'money' },
        { key: 'total', label: 'Total', type: 'money' },
    ],
    projects: [
        { key: 'number', label: 'Project #', type: 'string' },
        { key: 'name', label: 'Name', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'projectManager', label: 'Project Manager', type: 'string' },
        { key: 'isBillable', label: 'Billable', type: 'boolean' },
        { key: 'startDate', label: 'Start Date', type: 'date' },
        { key: 'endDate', label: 'End Date', type: 'date' },
    ],
    agreement_periods: [
        { key: 'agreement', label: 'Agreement', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'periodStart', label: 'Period Start', type: 'date' },
        { key: 'periodEnd', label: 'Period End', type: 'date' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'blockAllocatedHours', label: 'Allocated Hours', type: 'number' },
        { key: 'blockConsumedHours', label: 'Consumed Hours', type: 'number' },
        { key: 'blockOverageHours', label: 'Overage Hours', type: 'number' },
        { key: 'totalAmount', label: 'Total Amount', type: 'money' },
    ],
    tickets: [
        { key: 'number', label: 'Ticket #', type: 'string' },
        { key: 'title', label: 'Title', type: 'string' },
        { key: 'status', label: 'Status', type: 'string' },
        { key: 'priority', label: 'Priority', type: 'string' },
        { key: 'origin', label: 'Origin', type: 'string' },
        { key: 'project', label: 'Project', type: 'string' },
        { key: 'customer', label: 'Customer', type: 'string' },
        { key: 'createdAt', label: 'Created', type: 'date' },
    ],
};
// ---------------------------------------------------------------------------
// Filter catalog — the complete set of filters each entity may expose. Date-range
// filters are modeled as two keys (…From / …To). Any filter key not present here is
// rejected by the backend.
// ---------------------------------------------------------------------------
const YES_NO = ['yes', 'no'];
export const REPORT_FILTERS = {
    time_entries: [
        { key: 'workDateFrom', label: 'Work Date From', type: 'date' },
        { key: 'workDateTo', label: 'Work Date To', type: 'date' },
        { key: 'memberId', label: 'Member', type: 'uuid' },
        { key: 'projectId', label: 'Project', type: 'uuid' },
        { key: 'billable', label: 'Billable', type: 'enum', options: YES_NO },
    ],
    invoices: [
        { key: 'issueDateFrom', label: 'Issue Date From', type: 'date' },
        { key: 'issueDateTo', label: 'Issue Date To', type: 'date' },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['draft', 'in_review', 'sent', 'closed', 'void'],
        },
        {
            key: 'source',
            label: 'Source',
            type: 'enum',
            options: ['product_subscription', 'managed_services', 'project', 'tickets', 'manual'],
        },
        { key: 'customerId', label: 'Customer', type: 'uuid' },
    ],
    projects: [
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['active', 'on_hold', 'completed', 'cancelled'],
        },
        { key: 'customerId', label: 'Customer', type: 'uuid' },
        { key: 'projectManagerId', label: 'Project Manager', type: 'uuid' },
        { key: 'isBillable', label: 'Billable', type: 'enum', options: YES_NO },
    ],
    agreement_periods: [
        { key: 'periodStartFrom', label: 'Period Start From', type: 'date' },
        { key: 'periodStartTo', label: 'Period Start To', type: 'date' },
        { key: 'agreementId', label: 'Agreement', type: 'uuid' },
        {
            key: 'status',
            label: 'Status',
            type: 'enum',
            options: ['open', 'closed'],
        },
    ],
    tickets: [
        { key: 'createdFrom', label: 'Created From', type: 'date' },
        { key: 'createdTo', label: 'Created To', type: 'date' },
        { key: 'statusId', label: 'Status', type: 'uuid' },
        {
            key: 'priority',
            label: 'Priority',
            type: 'enum',
            options: ['low', 'medium', 'high', 'critical'],
        },
        { key: 'origin', label: 'Origin', type: 'enum', options: ['project', 'support'] },
        { key: 'projectId', label: 'Project', type: 'uuid' },
    ],
};
/** Returns the column catalog for an entity, or an empty list for an unknown entity. */
export function reportColumnsFor(entity) {
    return REPORT_COLUMNS[entity] ?? [];
}
/** Returns the filter catalog for an entity, or an empty list for an unknown entity. */
export function reportFiltersFor(entity) {
    return REPORT_FILTERS[entity] ?? [];
}
/** Maximum rows returned by an on-demand or scheduled run (safety cap). */
export const REPORT_MAX_ROWS = 5000;
//# sourceMappingURL=report.js.map